import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { DateOnlyPicker } from '@/shared/components/custom/form-field/DateOnlyPicker';
import { FIELD_TYPE } from '../types';
import type { FieldType } from '../types';
import type { CreateFieldInput, FieldOptionInput } from '../schemas';

interface DefaultValueFieldProps {
  fieldType: FieldType;
  control: Control<CreateFieldInput>;
  options: FieldOptionInput[];
  disabled?: boolean;
}

/**
 * Renders the appropriate default value control based on the selected field type.
 *
 * @remarks
 * - Returns `null` for `Users`, `Files`, and `Signature` — none of these can have a static default
 *   (Users needs the member picker spec; Files/Signature values are backend-constructed).
 * - Checkbox renders a Switch; the stored value is the string `"true"` or `"false"`.
 * - Select/MultiSelect renders a dropdown populated from `options`; returns `null` when no options exist yet.
 * - Number, Currency, and Date render type-constrained inputs. Email/Url/Phone render a plain text
 *   input with a type-appropriate HTML `type` for browser-level affordances (keyboard, autofill).
 */
export function DefaultValueField({
  fieldType,
  control,
  options,
  disabled,
}: DefaultValueFieldProps) {
  const { t } = useTranslation('customFields');
  const label = t('createForm.defaultValue');

  if (
    fieldType === FIELD_TYPE.Users ||
    fieldType === FIELD_TYPE.Files ||
    fieldType === FIELD_TYPE.Signature
  )
    return null;

  if (fieldType === FIELD_TYPE.Checkbox) {
    return (
      <Controller
        control={control}
        name="defaultValue"
        render={({ field }) => (
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="cf-default-value" className="text-sm cursor-pointer">
              {label}
            </Label>
            <Switch
              id="cf-default-value"
              checked={field.value === 'true'}
              onCheckedChange={(checked) => field.onChange(checked ? 'true' : 'false')}
              disabled={disabled}
              data-testid="custom-fields-default-value-input"
            />
          </div>
        )}
      />
    );
  }

  if (fieldType === FIELD_TYPE.Select || fieldType === FIELD_TYPE.MultiSelect) {
    const selectOptions = options
      .filter((o) => o.name)
      .map((o) => ({ value: o.value || o.name, label: o.name }));

    if (selectOptions.length === 0) return null;

    return (
      <FormFieldSelect
        control={control}
        name="defaultValue"
        label={label}
        options={selectOptions}
        floatingLabel
        disabled={disabled}
        data-testid="custom-fields-default-value-input"
      />
    );
  }

  if (fieldType === FIELD_TYPE.Number || fieldType === FIELD_TYPE.Currency) {
    return (
      <FormFieldInput
        control={control}
        name="defaultValue"
        label={label}
        type="number"
        floatingLabel
        disabled={disabled}
        data-testid="custom-fields-default-value-input"
      />
    );
  }

  if (
    fieldType === FIELD_TYPE.Email ||
    fieldType === FIELD_TYPE.Url ||
    fieldType === FIELD_TYPE.Phone
  ) {
    const inputType =
      fieldType === FIELD_TYPE.Email ? 'email' : fieldType === FIELD_TYPE.Url ? 'url' : 'tel';
    return (
      <FormFieldInput
        control={control}
        name="defaultValue"
        label={label}
        type={inputType}
        floatingLabel
        disabled={disabled}
        data-testid="custom-fields-default-value-input"
      />
    );
  }

  if (fieldType === FIELD_TYPE.Date) {
    return (
      <Controller
        control={control}
        name="defaultValue"
        render={({ field }) => (
          <DateOnlyPicker
            id="cf-default-value"
            label={label}
            value={field.value || null}
            onChange={(value) => field.onChange(value ?? '')}
            disabled={disabled}
            data-testid="custom-fields-default-value-input"
          />
        )}
      />
    );
  }

  return (
    <FormFieldInput
      control={control}
      name="defaultValue"
      label={label}
      floatingLabel
      disabled={disabled}
      data-testid="custom-fields-default-value-input"
    />
  );
}
